import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import { 
  ArrowLeft, 
  Loader2, 
  Clock,
  AlertTriangle,
  FileText,
  CheckCircle,
  Download,
  Mail,
  Edit,
  RotateCcw,
  Check,
  ImageIcon,
  XCircle,
  X,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', class: 'status-draft', icon: FileText },
  submitted: { label: 'Submitted', class: 'status-submitted', icon: Clock },
  returned: { label: 'Returned', class: 'status-returned', icon: AlertTriangle },
  approved: { label: 'Approved', class: 'status-approved', icon: CheckCircle },
  rejected: { label: 'Rejected', class: 'status-rejected', icon: XCircle },
};

export default function FormViewer() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isAdminOrManager } = useAuth();
  
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('1');
  
  // Admin actions
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returning, setReturning] = useState(false);
  
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Email dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // PDF
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Delete
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Revert to draft (for approved forms)
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [reverting, setReverting] = useState(false);

  const fetchForm = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/forms/${formId}`);
      setForm(response.data);
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [formId, navigate]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  const handleReturn = async () => {
    if (!returnReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    setReturning(true);
    try {
      await axios.post(`${API}/forms/${formId}/return`, { reason: returnReason });
      toast.success('Form sent back to user for editing');
      setShowReturnDialog(false);
      setReturnReason('');
      fetchForm();
    } catch (error) {
      toast.error('Failed to return form');
    } finally {
      setReturning(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setRejecting(true);
    try {
      await axios.post(`${API}/forms/${formId}/reject`, { reason: rejectReason });
      toast.success('Form rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      fetchForm();
    } catch (error) {
      toast.error('Failed to reject form');
    } finally {
      setRejecting(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(`${API}/forms/${formId}/approve`);
      toast.success('Form approved successfully');
      setShowApproveDialog(false);
      fetchForm();
    } catch (error) {
      toast.error('Failed to approve form');
    } finally {
      setApproving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/send-form`, {
        form_id: formId,
        recipient_email: recipientEmail
      });
      toast.success(`Form sent to ${recipientEmail}`);
      setShowEmailDialog(false);
      setRecipientEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleExportPdf = async () => {
    if (!form) return;
    
    setGeneratingPdf(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = 20;
      
      // Header
      pdf.setFontSize(24);
      pdf.setTextColor(0, 86, 210);
      pdf.text('QA Builder Pro', margin, yPos);
      yPos += 10;
      
      pdf.setFontSize(18);
      pdf.setTextColor(51, 65, 85);
      pdf.text(form.project_name, margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(12);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`${form.trade_type} | Status: ${form.status.toUpperCase()}`, margin, yPos);
      yPos += 15;
      
      // Form details
      pdf.setFontSize(10);
      pdf.setTextColor(51, 65, 85);
      
      const details = [
        `Timestamp: ${format(new Date(form.timestamp), 'PPpp')}`,
        `Created by: ${form.created_by_name}`,
        `Individual: ${form.individual_name || 'N/A'}`,
        `Date: ${form.individual_date || 'N/A'}`,
        `Location: ${form.location || 'N/A'}`,
      ];
      
      details.forEach(detail => {
        pdf.text(detail, margin, yPos);
        yPos += 6;
      });
      
      if (form.notes) {
        yPos += 5;
        pdf.text('Notes:', margin, yPos);
        yPos += 5;
        const splitNotes = pdf.splitTextToSize(form.notes, pageWidth - margin * 2);
        pdf.text(splitNotes, margin, yPos);
        yPos += splitNotes.length * 5;
      }
      
      // Signature
      if (form.signature_data) {
        yPos += 10;
        pdf.text('Signature:', margin, yPos);
        yPos += 5;
        try {
          pdf.addImage(form.signature_data, 'PNG', margin, yPos, 60, 25);
        } catch (e) {
          console.error('Failed to add signature image');
        }
      }
      
      // Pages with photos
      for (let pageIdx = 0; pageIdx < form.pages.length; pageIdx++) {
        pdf.addPage();
        yPos = 20;
        
        pdf.setFontSize(14);
        pdf.setTextColor(0, 86, 210);
        pdf.text(`Page ${pageIdx + 1}`, margin, yPos);
        yPos += 10;
        
        const page = form.pages[pageIdx];
        const imgWidth = 80;
        const imgHeight = 60;
        
        for (let slotIdx = 0; slotIdx < page.slots.length; slotIdx++) {
          const slot = page.slots[slotIdx];
          
          if (yPos > 250) {
            pdf.addPage();
            yPos = 20;
          }
          
          pdf.setFontSize(10);
          pdf.setTextColor(100, 116, 139);
          pdf.text(`Slot ${slotIdx + 1}`, margin, yPos);
          yPos += 5;
          
          if (slot.image_data) {
            try {
              pdf.addImage(slot.image_data, 'JPEG', margin, yPos, imgWidth, imgHeight);
              yPos += imgHeight + 3;
            } catch (e) {
              pdf.text('[Image could not be loaded]', margin, yPos);
              yPos += 10;
            }
          } else {
            pdf.setDrawColor(200);
            pdf.rect(margin, yPos, imgWidth, imgHeight);
            pdf.text('No image', margin + 30, yPos + 30);
            yPos += imgHeight + 3;
          }
          
          if (slot.description) {
            pdf.setFontSize(9);
            pdf.setTextColor(51, 65, 85);
            const splitDesc = pdf.splitTextToSize(slot.description, pageWidth - margin * 2);
            pdf.text(splitDesc, margin, yPos);
            yPos += splitDesc.length * 4 + 5;
          }
          
          yPos += 5;
        }
      }
      
      pdf.save(`${form.project_name.replace(/[^a-z0-9]/gi, '_')}_QA_Form.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/forms/${formId}`);
      toast.success('Form deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRevertToDraft = async () => {
    if (!revertReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    setReverting(true);
    try {
      await axios.post(`${API}/forms/${formId}/revert`, { reason: revertReason });
      toast.success('Form reverted to draft and sent back to user');
      setShowRevertDialog(false);
      setRevertReason('');
      fetchForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert form');
    } finally {
      setReverting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!form) return null;

  const statusInfo = statusConfig[form.status];
  const StatusIcon = statusInfo.icon;
  const canTakeAction = isAdminOrManager() && form.status === 'submitted';
  const canRevertApproved = isAdminOrManager() && form.status === 'approved';

  return (
    <div className="min-h-screen bg-background" data-testid="form-viewer-page">
      <Header />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  {form.company_name || form.project_name}
                </h1>
                <Badge className={statusInfo.class}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusInfo.label}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">
                {form.trade_type}{form.form_type ? ` - ${form.form_type}` : ''} • {form.site_name || 'No site'} • Created by {form.created_by_name}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                onClick={handleExportPdf}
                disabled={generatingPdf}
                data-testid="export-pdf-btn"
              >
                {generatingPdf ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Export PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowEmailDialog(true)}
                data-testid="send-email-btn"
              >
                <Mail className="mr-2 h-4 w-4" />
                Send Email
              </Button>
              
              {isAdminOrManager() && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate(`/forms/${formId}/edit`)}
                    data-testid="edit-btn"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-btn"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Status Messages */}
          {form.return_reason && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Form Returned for Editing</p>
                  <p className="text-sm text-amber-700 mt-1">{form.return_reason}</p>
                </div>
              </div>
            </div>
          )}

          {form.reject_reason && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Form Rejected</p>
                  <p className="text-sm text-red-700 mt-1">{form.reject_reason}</p>
                </div>
              </div>
            </div>
          )}

          {form.status === 'approved' && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">Form Approved</p>
                    <p className="text-sm text-green-700 mt-1">This form has been reviewed and approved.</p>
                  </div>
                </div>
                {canRevertApproved && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRevertDialog(true)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="revert-to-draft-btn"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Revert to Draft
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Admin Actions Card */}
        {canTakeAction && (
          <Card className="mb-6 border-2 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Review Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                This form is awaiting your review. Please review the details below and take action.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={() => setShowApproveDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="approve-btn"
                >
                  <Check className="mr-2 h-4 w-4" />
                  Approve Form
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowReturnDialog(true)}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="return-btn"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Send Back to Edit
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowRejectDialog(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="reject-btn"
                >
                  <X className="mr-2 h-4 w-4" />
                  Reject Form
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Form Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Timestamp</Label>
                <p className="font-medium">{format(new Date(form.timestamp), 'PPpp')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Trade Type</Label>
                <p className="font-medium">{form.trade_type}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Individual Name</Label>
                <p className="font-medium">{form.individual_name || '-'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Date</Label>
                <p className="font-medium">{form.individual_date || '-'}</p>
              </div>
            </div>
            
            {form.signature_data && (
              <div>
                <Label className="text-muted-foreground text-xs">Signature</Label>
                <div className="mt-2 border rounded-lg p-2 bg-white inline-block">
                  <img 
                    src={form.signature_data} 
                    alt="Signature" 
                    className="max-h-20"
                  />
                </div>
              </div>
            )}
            
            {form.notes && (
              <div>
                <Label className="text-muted-foreground text-xs">Notes</Label>
                <p className="mt-1 text-sm whitespace-pre-wrap">{form.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photo Slots */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Inspection Photos</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={currentPage} onValueChange={setCurrentPage}>
              <TabsList className="grid w-full grid-cols-3 mb-6" style={{ gridTemplateColumns: `repeat(${Math.min(form.pages.length, 6)}, 1fr)` }}>
                {form.pages.map((_, index) => (
                  <TabsTrigger key={index} value={String(index + 1)} data-testid={`view-page-${index + 1}-tab`}>
                    Page {index + 1}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {form.pages.map((page, pageIndex) => (
                <TabsContent key={pageIndex} value={String(pageIndex + 1)}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {page.slots.map((slot, slotIndex) => (
                      <div key={slot.slot_id} className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground">
                          Slot {slotIndex + 1}
                        </Label>
                        <div className="border rounded-lg overflow-hidden bg-white">
                          {slot.image_data ? (
                            <img 
                              src={slot.image_data} 
                              alt={`Slot ${slotIndex + 1}`}
                              className="w-full h-48 object-cover"
                            />
                          ) : (
                            <div className="w-full h-48 bg-slate-100 flex items-center justify-center">
                              <div className="text-center text-muted-foreground">
                                <ImageIcon className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-sm">No image</p>
                              </div>
                            </div>
                          )}
                        </div>
                        {slot.description && (
                          <p className="text-sm text-muted-foreground p-3 bg-slate-50 rounded-md">
                            {slot.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </main>

      {/* Approve Confirmation Dialog */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Approve Form?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the form as approved. The user will be notified of the approval.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleApprove}
              disabled={approving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-approve-btn"
            >
              {approving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-600" />
              Send Back for Editing
            </DialogTitle>
            <DialogDescription>
              The user will be notified and able to make changes to this form. They can then resubmit it for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="return-reason">What needs to be corrected? *</Label>
              <Textarea
                id="return-reason"
                placeholder="Please explain what changes are needed..."
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="return-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReturn}
              disabled={returning}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-return-btn"
            >
              {returning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
              Send Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Reject Form
            </DialogTitle>
            <DialogDescription>
              This will permanently reject the form. The user will be notified but will NOT be able to edit or resubmit this form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Reason for rejection *</Label>
              <Textarea
                id="reject-reason"
                placeholder="Please explain why this form is being rejected..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReject}
              disabled={rejecting}
              variant="destructive"
              data-testid="confirm-reject-btn"
            >
              {rejecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}
              Reject Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Form via Email</DialogTitle>
            <DialogDescription>
              Send a summary of this form to an email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="recipient@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                data-testid="recipient-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail}
              data-testid="confirm-send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Form
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this form? This action cannot be undone. 
              All data, images, and signatures associated with this form will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete Form
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revert to Draft Dialog */}
      <Dialog open={showRevertDialog} onOpenChange={setShowRevertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RotateCcw className="h-5 w-5" />
              Revert to Draft
            </DialogTitle>
            <DialogDescription>
              This will change the approved form back to draft status. The user will be notified and can make changes before resubmitting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="revert-reason">Reason for reverting *</Label>
              <Textarea
                id="revert-reason"
                placeholder="Please explain what needs to be changed..."
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="revert-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRevertToDraft}
              disabled={reverting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-revert-btn"
            >
              {reverting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
              Revert to Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
